import { USER_ROLES, ROLE_PERMISSIONS, NAVIGATION_ITEMS } from './constants';

/**
 * Role-Based Access Control (RBAC) Utility
 * - Small helpers used by navigation and role guards to determine
 *   available actions and route access for the logged-in user.
 */

/**
 * Check if a user has a specific permission
 * @param {string} userRole - User's role
 * @param {string} permission - Permission to check
 * @returns {boolean} - Whether user has the permission
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;

  // Super Admin has all permissions
  if (userRole === USER_ROLES.SUPER_ADMIN) return true;

  // Other roles check the permission mapping
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  return rolePermissions.includes(permission);
};

// ...existing code...

/**
 * Check if a user has any of the specified permissions
 * @param {string} userRole - User's role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has at least one permission
 */
export const hasAnyPermission = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;

  return permissions.some(permission => hasPermission(userRole, permission));
};

/**
 * Check if a user has all of the specified permissions
 * @param {string} userRole - User's role
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - Whether user has all permissions
 */
export const hasAllPermissions = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;

  return permissions.every(permission => hasPermission(userRole, permission));
};

/**
 * Get all permissions for a user role
 * @param {string} userRole - User's role
 * @returns {string[]} - Array of permissions
 */
export const getUserPermissions = (userRole) => {
  if (!userRole) return [];

  return ROLE_PERMISSIONS[userRole] || [];
};

/**
 * Check if a user can access a specific route/page
 * @param {string} userRole - User's role
 * @param {string} path - Route path to check
 * @returns {boolean} - Whether user can access the route
 */
export const canAccessRoute = (userRole, path) => {
  if (!userRole || !path) return false;

  // Find navigation item by path
  const navItem = Object.values(NAVIGATION_ITEMS).find(item => item.path === path);

  if (!navItem) {
    // If route is not in navigation items, allow access (public route)
    return true;
  }

  // Check if user has required permissions
  return hasAnyPermission(userRole, navItem.requiredPermissions);
};

/**
 * Get filtered navigation items based on user role
 * @param {string} userRole - User's role
 * @returns {object[]} - Array of accessible navigation items
 */
export const getAccessibleNavigation = (userRole) => {
  if (!userRole) return [];

  return Object.entries(NAVIGATION_ITEMS)
    .filter(([key, item]) => hasAnyPermission(userRole, item.requiredPermissions))
    .map(([key, item]) => ({
      key,
      ...item,
    }));
};

/**
 * Check if a user is a super admin
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether user is super admin
 */
export const isSuperAdmin = (userRole) => {
  return userRole === USER_ROLES.SUPER_ADMIN;
};

/**
 * Check if a user is an admin (admin or super admin)
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether user is admin or super admin
 */
export const isAdmin = (userRole) => {
  return userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
};

/**
 * Get role hierarchy level (higher number = more permissions)
 * @param {string} userRole - User's role
 * @returns {number} - Role level
 */
export const getRoleLevel = (userRole) => {
  const roleLevels = {
    // Custom role hierarchy based on system roles
    // Lower number = lower privileges
    [USER_ROLES.LABOUR]: 1,
    [USER_ROLES.SALES_STAFF]: 2,
    [USER_ROLES.PURCHASE_STAFF]: 2,
    [USER_ROLES.WAREHOUSE_STAFF]: 2,
    [USER_ROLES.ACCOUNT_USER]: 3,
    [USER_ROLES.DELIVERY_SUPERVISOR]: 4,
    // Combined operational role — sits above the individual operational roles
    // but below Admin.
    [USER_ROLES.OPERATIONS_MANAGER]: 4,
    [USER_ROLES.ADMIN]: 5,
    [USER_ROLES.MANAGER]: 5,
    [USER_ROLES.SUPER_ADMIN]: 6,
    [USER_ROLES.OWNER]: 6,
  };

  return roleLevels[userRole] || 0;
};

/**
 * Check if user role has higher or equal level than required role
 * @param {string} userRole - User's role
 * @param {string} requiredRole - Required role
 * @returns {boolean} - Whether user meets role requirement
 */
export const meetsRoleRequirement = (userRole, requiredRole) => {
  return getRoleLevel(userRole) >= getRoleLevel(requiredRole);
};

/**
 * Check if a user can delete records
 * @param {string} userRole - User's role
 * @returns {boolean} - Whether user can delete
 */
export const canDelete = (userRole) => {
  // Only Admin and Super Admin can delete
  return userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
};

/**
 * Check if a user can edit a record
 * @param {string} userRole - User's role
 * @param {string|Date} recordDate - Date the record was created
 * @param {object} options - Additional options (e.g., { isMasterData: true })
 * @returns {boolean} - Whether user can edit
 */
export const canEdit = (userRole, recordDate, options = {}) => {
  if (!userRole) return false;

  // Super Admin can edit everything
  if (userRole === USER_ROLES.SUPER_ADMIN) return true;

  // For Admin, Sales Staff, and Operations Manager roles
  if (
    userRole === USER_ROLES.ADMIN ||
    userRole === USER_ROLES.SALES_STAFF ||
    userRole === USER_ROLES.OPERATIONS_MANAGER
  ) {
    // Master data, inquiries, orders, or purchase orders can be edited regardless of date
    if (options.isMasterData || options.isInquiry || options.isOrder || options.isPurchaseOrder) return true;

    if (!recordDate) return true; // Default to true if no date provided

    const today = new Date().toISOString().split('T')[0];
    const itemDate = new Date(recordDate).toISOString().split('T')[0];
    return today === itemDate;
  }

  return false;
};

/**
 * Filter data based on user permissions
 * @param {any[]} data - Data array to filter
 * @param {string} userRole - User's role
 * @param {function} permissionCheck - Function to check permission for each item
 * @returns {any[]} - Filtered data array
 */
export const filterDataByPermissions = (data, userRole, permissionCheck) => {
  if (!data || !Array.isArray(data)) return [];

  if (isSuperAdmin(userRole)) {
    // Super admin can see everything
    return data;
  }

  return data.filter(item => permissionCheck(userRole, item));
};